import { useContext } from 'react';
import noop from 'lodash/noop';
import { Product, AbsProduct, HycProduct, IgcProduct, ProductAbbreviations } from '@/creditflowresearch/domain/Product';
import { AbsContext } from '@/creditflowresearch/application/Deal/contexts/IssueTile/AbsContext';
import { CorporateContext } from '@/creditflowresearch/application/Deal/contexts/IssueTile/CorporateContext';

const contextHandlers = [
  {
    matches: (abbreviation: ProductAbbreviations) => abbreviation === AbsProduct.ABBREVIATION,
    getContext: (contexts: Record<string, any>) => contexts.abs,
  },
  {
    matches: (abbreviation: ProductAbbreviations) => [
      HycProduct.ABBREVIATION,
      IgcProduct.ABBREVIATION,
    ].includes(abbreviation),
    getContext: (contexts: Record<string, any>) => contexts.corporate,
  },
];

const DEFAULT_CONTEXT_VALUE = {
  subscriptions: null,
  markSubscribed: noop,
  isFetching: false,
  collection: [],
  totalCount: 0,
  pageFilters: {},
};

const getContextForProduct = (abbreviation: ProductAbbreviations, contexts: Record<string, any>) => {
  const handler = contextHandlers.find((h) => h.matches(abbreviation));
  if (!handler) {
    return null;
  }

  const context = handler.getContext(contexts);
  return context || null;
};

export const useDealContext = (activeProduct: string | string[]) => {
  const absContext = useContext(AbsContext);
  const corporateContext = useContext(CorporateContext);

  if (!activeProduct || (Array.isArray(activeProduct) && activeProduct.length === 0)) {
    return DEFAULT_CONTEXT_VALUE;
  }

  const productIds = Array.isArray(activeProduct) ? activeProduct : [activeProduct];

  const validProductIds = productIds.filter((id) => id != null && typeof id === 'string');

  if (validProductIds.length === 0) {
    return DEFAULT_CONTEXT_VALUE;
  }

  const contexts = {
    abs: absContext,
    corporate: corporateContext,
  };

  const validProduct = validProductIds.find((productId) => Product.getAbbreviation(productId));

  if (!validProduct) {
    return DEFAULT_CONTEXT_VALUE;
  }

  const abbreviation = Product.getAbbreviation(validProduct);
  const context = getContextForProduct(abbreviation, contexts);

  return context || DEFAULT_CONTEXT_VALUE;
};
